/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Calendar, TrendingUp, CheckCircle, Euro, Clock, Award, Users } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { dashboardAPI } from '../api/dashboard';
import {  } from '../utils/date';
import { fmtDurataOre } from '../utils/durata';

const ACCENT = '#8B5CF6';

const PRIORITA_COLORS = {
  urgente: '#DC2626',
  alta:    '#EA580C',
  normale: '#3B82F6',
  bassa:   '#6B7280',
};

const STATO_LABELS = {
  in_attesa: 'In attesa', confermato: 'Confermato',
  in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato',
};

const PRIORITA_LABELS = {
  urgente: 'Urgente', alta: 'Alta', normale: 'Normale', bassa: 'Bassa',
};

// Preset periodi
const PRESETS = [
  { label: 'Oggi',        days: 0 },
  { label: 'Ultimi 7gg',  days: 7 },
  { label: 'Ultimi 30gg', days: 30 },
  { label: 'Ultimi 90gg', days: 90 },
  { label: 'Anno',        days: 365 },
];

export default function Report() {
  const [periodo, setPeriodo] = useState({
    da: toDateInput(daysAgo(30)),
    a: toDateInput(new Date()),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['report', periodo],
    queryFn: () => dashboardAPI.report(periodo),
  });

  const applyPreset = (days) => {
    setPeriodo({
      da: toDateInput(daysAgo(days)),
      a: toDateInput(new Date()),
    });
  };

  return (
    <AdminLayout title="Report">
      {/* Selettore periodo */}
      <div style={styles.periodoBar}>
        <div style={styles.presets}>
          {PRESETS.map((p) => {
            const active = periodo.da === toDateInput(daysAgo(p.days)) && periodo.a === toDateInput(new Date());
            return (
              <button
                key={p.label}
                onClick={() => applyPreset(p.days)}
                style={{ ...styles.presetBtn, ...(active ? styles.presetBtnActive : {}) }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div style={styles.dateRange}>
          <Calendar size={16} color="#6B7280" />
          <input
            type="date"
            value={periodo.da}
            onChange={(e) => setPeriodo({ ...periodo, da: e.target.value })}
            style={styles.dateInput}
          />
          <span style={{ color: '#9CA3AF' }}>→</span>
          <input
            type="date"
            value={periodo.a}
            onChange={(e) => setPeriodo({ ...periodo, a: e.target.value })}
            style={styles.dateInput}
          />
        </div>
      </div>

      {isLoading && <div style={styles.loading}>Caricamento report...</div>}
      {error && <div style={styles.errorCard}>Errore nel caricamento.</div>}

      {data && (
        <>
          {/* KPI principali */}
          <div style={styles.kpiGrid}>
            <KpiCard icon={TrendingUp} label="Interventi totali" value={data.kpi.totale_interventi} color="#8B5CF6" />
            <KpiCard icon={CheckCircle} label="Completati" value={data.kpi.completati} color="#10B981"
                     subtext={data.kpi.totale_interventi > 0 ? `${Math.round(data.kpi.completati / data.kpi.totale_interventi * 100)}%` : '—'} />
            <KpiCard icon={Euro} label="Fatturato" value={`€ ${data.kpi.fatturato.toFixed(2)}`} color="#059669" />
            <KpiCard icon={Clock} label="Ore lavorate" value={fmtDurataOre(data.kpi.ore_totali)} color="#3B82F6" />
            <KpiCard icon={Clock} label="Durata media" value={fmtDurataOre(data.kpi.durata_media_ore)} color="#6366F1"
                     subtext="da inizio a fine" />
            <KpiCard icon={Award} label="Annullati" value={data.kpi.annullati} color="#EF4444" />
          </div>

          {/* Andamento giornaliero */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Andamento giornaliero</h3>
            {data.andamento_giornaliero.length === 0 ? (
              <div style={styles.empty}>Nessun intervento nel periodo selezionato.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.andamento_giornaliero.map(d => ({
                  ...d,
                  giorno: new Date(d.giorno).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="giorno" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="creati"     stroke={ACCENT}    strokeWidth={2} name="Creati" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="completati" stroke="#10B981"   strokeWidth={2} name="Completati" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Due colonne: per stato e per priorità */}
          <div style={styles.twoColumns}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Distribuzione per stato</h3>
              {data.per_stato.length === 0 ? (
                <div style={styles.empty}>Nessun dato</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.per_stato}
                      dataKey="totale"
                      nameKey="stato"
                      cx="50%" cy="50%"
                      outerRadius={90}
                      label={(e) => `${STATO_LABELS[e.stato] || e.stato}: ${e.totale}`}
                    >
                      {data.per_stato.map((entry, idx) => (
                        <Cell key={idx} fill={entry.colore} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, STATO_LABELS[name] || name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Distribuzione per priorità</h3>
              {data.per_priorita.length === 0 ? (
                <div style={styles.empty}>Nessun dato</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.per_priorita}
                      dataKey="totale"
                      nameKey="priorita"
                      cx="50%" cy="50%"
                      outerRadius={90}
                      label={(e) => `${PRIORITA_LABELS[e.priorita] || e.priorita}: ${e.totale}`}
                    >
                      {data.per_priorita.map((entry, idx) => (
                        <Cell key={idx} fill={PRIORITA_COLORS[entry.priorita] || '#6B7280'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, PRIORITA_LABELS[name] || name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Performance tecnici */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <Users size={18} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Performance tecnici
            </h3>
            {data.per_tecnico.length === 0 ? (
              <div style={styles.empty}>Nessun intervento con tecnico assegnato nel periodo.</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(120, data.per_tecnico.length * 50)}>
                <BarChart
                  data={data.per_tecnico.map(t => ({ ...t, nominativo: `${t.nome} ${t.cognome}` }))}
                  layout="vertical"
                  margin={{ left: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="nominativo" tick={{ fontSize: 12 }} width={120} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }} />
                  <Legend />
                  <Bar dataKey="interventi_totali" fill={ACCENT}  name="Totali" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="completati"        fill="#10B981" name="Completati" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Top clienti */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Top clienti per attività</h3>
            {data.top_clienti.length === 0 ? (
              <div style={styles.empty}>Nessun cliente con interventi nel periodo.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    <th style={styles.th}>Cliente</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Interventi</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Fatturato</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_clienti.map((c, idx) => (
                    <tr key={c.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {idx < 3 && <span style={styles.medal}>{['🥇','🥈','🥉'][idx]}</span>}
                          <div>
                            <div style={{ fontWeight: 600 }}>{c.nome} {c.cognome}</div>
                            <div style={{ fontSize: '0.78rem', color: '#6B7280' }}>{c.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{c.interventi_totali}</td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: c.fatturato > 0 ? '#059669' : '#9CA3AF' }}>
                        € {c.fatturato.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Tipo problema */}
          {data.per_tipo_problema.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Tipologie di problema</h3>
              <ResponsiveContainer width="100%" height={Math.max(120, data.per_tipo_problema.length * 40)}>
                <BarChart
                  data={data.per_tipo_problema}
                  layout="vertical"
                  margin={{ left: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="tipo" tick={{ fontSize: 12 }} width={150} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }} />
                  <Bar dataKey="totale" fill={ACCENT} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

function KpiCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ ...styles.kpiIconBox, backgroundColor: color + '20', color }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={styles.kpiLabel}>{label}</div>
        <div style={styles.kpiValue}>{value}</div>
        {subtext && <div style={styles.kpiSubtext}>{subtext}</div>}
      </div>
    </div>
  );
}

function daysAgo(days) {
  return new Date(Date.now() - days * 24 * 3600 * 1000);
}

function toDateInput(date) {
  if (typeof date === 'string') return date;
  return date.toISOString().slice(0, 10);
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  errorCard: { backgroundColor: 'white', padding: '2rem', borderRadius: '12px', textAlign: 'center', color: '#991B1B' },

  periodoBar: { backgroundColor: 'white', padding: '1rem 1.25rem', borderRadius: '12px', marginBottom: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
  presets: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  presetBtn: { padding: '0.45rem 0.85rem', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, fontFamily: 'inherit' },
  presetBtnActive: { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT },
  dateRange: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  dateInput: { padding: '0.5rem 0.7rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit' },

  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.85rem', marginBottom: '1.25rem' },
  kpiCard: { display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '1.1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  kpiIconBox: { width: '42px', height: '42px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: '0.76rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 },
  kpiValue: { fontSize: '1.4rem', fontWeight: 700, color: '#111827', marginTop: '0.15rem' },
  kpiSubtext: { fontSize: '0.78rem', color: '#9CA3AF', marginTop: '0.1rem' },

  section: { backgroundColor: 'white', padding: '1.25rem 1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  sectionTitle: { margin: '0 0 1rem', fontSize: '1.05rem', fontWeight: 700, color: '#111827' },
  twoColumns: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' },
  empty: { padding: '2rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.9rem' },

  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
  tr: { borderTop: '1px solid #F3F4F6' },
  td: { padding: '0.75rem 1rem', fontSize: '0.92rem', color: '#374151' },
  medal: { fontSize: '1.25rem' },
};
